<template>
  <div class="c-dz">
    <vue-dropzone
      ref="dz"
      :id="id"
      :options="dropzoneOptions"
      @vdropzone-duplicate-file="onDuplicate"
      @vdropzone-file-added="onFileAdded"
      @vdropzone-sending="onSending"
      @vdropzone-success="onSuccess"
      @vdropzone-queue-complete="onQueueCompleted"
      @vdropzone-error="onSendError"
      @vdropzone-canceled="onCanceled"
      @vdropzone-thumbnail="onThumbnail"
    />

    <div class="c-flex w100 c-btm">
      <div class="s">
        <div v-for="errMsg in errMsgs" class="c-msg-area v-messages__message error--text">{{ errMsg }}</div>
        <div v-if="targetExts" class="c-msg-area c-hint  v-messages__message">
          {{ $T('page.album.hint.acceptables', { exts: targetExts }) }}
        </div>
        <div v-if="forAlbum" class="c-msg-area c-hint  v-messages__message">{{ $T('page.album.hint.compress') }}</div>
      </div>
    </div>
    <div class="c-erase">
      <v-tooltip left>
        <v-icon slot="activator" color="clr-primary" :disabled="uploading" @click="clearFiles">mdi-eraser</v-icon>
        <span>{{ $T('common.clear_all') }}</span>
      </v-tooltip>
    </div>
  </div>
</template>

<script>
import { CONST } from '../../constant';
import vue2Dropzone from 'vue2-dropzone';
import 'vue2-dropzone/dist/vue2Dropzone.min.css';
import hashUtils from '../../utils/hashUtils.js';
import imageUtils from '../../utils/imageUtils.js';
import { hasDuplicateFile } from '../../api/file';

export default {
  props: {
    id: { type: String, required: true },
    cid: { type: Number, required: false },
    type: { type: String, required: true, default: 'file' },
    uploadUrl: { type: String, required: true },
    quality: { type: Number, required: false, default: 0.8 },
    thumbnailWidth: { type: Number, required: false, default: 120 },
    thumbnailHeight: { type: Number, required: false, default: 120 },
    maxFileCount: { type: Number, required: false, default: 50 },
    forAlbum: { type: Boolean, required: false, default: false },
    checkDup: { type: Boolean, required: false, default: true },
    parallelUploads: { type: Number, required: false, default: 2 },
  },
  components: {
    vueDropzone: vue2Dropzone,
  },
  watch: {
    checkDup(newVal, oldVal) {
      this.dropzoneOptions.checkDup = newVal;
      var _dz = this.getNativeDropZone();
      var _op = _dz.options;
      _op.checkDup = newVal;
    },
    uploading(n, o) {
      this.$store.commit('UPLOADING', n);
    },
    cid(n, o) {
      this.dropzoneOptions.cid = n;
      var _dz = this.getNativeDropZone();
      var _op = _dz.options;
      _op.cid = n;
    },
  },
  created() {},
  data() {
    return {
      uploading: false,
      volume: null,
      errMsgs: {},
      errMsgList: [],
      dropzoneOptions: {
        url: this.uploadUrl,
        headers: {
          // The Spring backend validates CSRF from the X-XSRF-TOKEN header against the XSRF-TOKEN
          // cookie (axios does this automatically; dropzone uses its own XHR so we set it here).
          // The old X-CSRF-TOKEN (from a non-existent <meta>) was ignored and uploads got 403.
          'X-XSRF-TOKEN': decodeURIComponent((document.cookie.match(/(?:^|;\s*)XSRF-TOKEN=([^;]*)/) || [])[1] || ''),
        },
        cid: this.cid,
        chunking: true,
        chunkSize: 1000000,
        parallelChunkUploads: false,
        acceptedFiles: this.targetExts,
        autoProcessQueue: false,
        duplicateCheck: true,
        parallelUploads: this.parallelUploads,
        createImageThumbnails: true,
        forAlbum: this.forAlbum,
        checkDup: this.checkDup,
        maxFilesize: this.forAlbum ? CONST.accept_size_mb.movie : CONST.accept_size_mb.other,
        dictDefaultMessage: $T('page.album.dz.dictDefaultMessage'),
        dictFallbackMessage: $T('page.album.dz.dictFallbackMessage'),
        dictFallbackText: $T('page.album.dz.dictFallbackText'),
        dictFileTooBig: $T('page.album.dz.dictFileTooBig'),
        dictInvalidFileType: $T('page.album.dz.dictInvalidFileType'),
        dictResponseError: $T('page.album.dz.dictResponseError'),
        dictCancelUpload: $T('page.album.dz.dictCancelUpload'),
        dictUploadCanceled: $T('page.album.dz.dictUploadCanceled'),
        dictCancelUploadConfirmation: $T('page.album.dz.dictCancelUploadConfirmation'),
        dictRemoveFile: $T('page.album.dz.dictRemoveFile'),
        dictMaxFilesExceeded: $T('page.album.dz.dictMaxFilesExceeded'),
        previewTemplate: this.template(),
        isImg: function(file) {
          return !!CONST.accept_album_img_exts[file.ext];
        },
        isMovie: function(file) {
          return !!CONST.accept_album_movie_exts[file.ext];
        },
        cancelProcessing: function(_dz, file, msg, stopUploading) {
          file.cancelMsg = msg;

          // hack (not to stop file of after this file)
          // https://github.com/enyo/dropzone/blob/08b9e0a763b54a685404dea523a9c54242fbe1b9/dist/dropzone.js
          // line 2207 AND 2247
          if (file.status == 'uploading') {
            file.status = 'queued';
          }
          _dz.cancelUpload(file);

          _dz.options.removePreparing(file);
          if (!stopUploading) {
            _dz.processQueue();
          }
        },
        accept: function(file, done) {
          // 'this' is dropzone element
          var _dz = this;
          var _op = _dz.options;
          var _done = done;
          done = function(msg) {
            _op.removePreparing(file);
            if (msg) {
              _done(msg);
            } else {
              _done();
            }
          };

          // size
          if (!file.size) {
            done($T('page.album.err.size-0'));
            return;
          }
          file.origSize = file.size;
          // name
          if (!file.name || file.name.startsWith('.')) {
            done($T('page.album.err.name'));
            return;
          }
          file.origName = file.name;

          // add token
          file.token = new Date().getTime() + '_' + randomHexString(8);

          // last modified
          file.origLastModified = file.lastModified
            ? file.lastModified
            : file.lastModifiedDate
            ? file.lastModifiedDate.getTime()
            : 0;

          // get ext
          var comps = file.name.split('.');
          if (comps.length === 1 || (comps[0] === '' && comps.length === 2)) {
            done($T('page.album.err.name'));
            return;
          }
          file.ext = comps.pop().toLowerCase();

          // Reject unsupported types first. (Previously the size-check block below left sizeResult
          // undefined for a non-image/non-movie album file and `return`ed WITHOUT calling done(),
          // leaving the file stuck "preparing" forever — the spinner never cleared and the file was
          // never queued, so upload reported "no file selected".)
          if (_op.forAlbum && !_op.isImg(file) && !_op.isMovie(file)) {
            done($T('page.album.dz.dictInvalidFileType'));
            return;
          }

          // size check
          var sizeResult;
          if (_op.forAlbum) {
            if (_op.isImg(file)) {
              sizeResult = _op.checkSize(file, done, CONST.accept_size_mb.img, 'page.album.err.max.filesize.img');
            } else {
              sizeResult = _op.checkSize(file, done, CONST.accept_size_mb.movie, 'page.album.err.max.filesize.movie');
            }
          } else {
            sizeResult = _op.checkSize(file, done, CONST.accept_size_mb.other, 'page.album.err.max.filesize.img');
          }
          if (!sizeResult) return;

          done();
        },
        checkSize(file, done, mb, msgKey) {
          if (file.size > mb * 1024 * 1024) {
            done($T(msgKey, { mb: mb }));
            return false;
          }
          return true;
        },
        transformFile: function(file, done) {
          // 'this' is dropzone element
          var _dz = this;
          var _op = _dz.options;
          var _done = done;
          done = function(msg) {
            _op.removePreparing(file);
            if (msg) {
              _done(msg);
            } else {
              _done();
            }
          };
          _op.addPreparing(file);

          // set logger
          file.start = function(key) {
            if (file.timer) {
              console.log(
                file.origName + ' ' + file.timer.k + ' was NOT end (' + (new Date().getTime() - file.timer.t) + ')'
              );
            }
            file.timer = { k: key, t: new Date().getTime() };
            console.log(file.origName + ' ' + key + ' start!');
          };
          file.end = function() {
            console.log(file.origName + ' ' + file.timer.k + ' end (' + (new Date().getTime() - file.timer.t) + ')');
            file.timer = null;
          };

          // get exif
          var loadComplete = false;

          _op.readMd5(file, done, _dz, _op);
        },
        readMd5: function(file, done, _dz, _op) {
          file.start('md5');
          hashUtils.getMd5(
            file,
            function(md5) {
              file.end();
              file.md5 = md5;
              file.start('check duplication');
              _op.checkDuplication(file, _dz, _op, function() {
                file.end();
                done(file);
              });
            },
            function(e) {
              console.error('failed to read md5', e);
              _op.cancelProcessing(_dz, file, $T('page.album.err.preparing'));
            }
          );
        },
        checkDuplication: function(file, _dz, _op, next) {
          if (!_op.checkDup) {
            file.checkDupPassed = true;
            return next();
          }

          // check client files.
          var files = _dz.getAcceptedFiles();
          for (var _file of files) {
            if (_file === files) {
              continue;
            }
            if (_file.md5 == file.md5 && _file.size == file.size && _file.checkDupPassed) {
              console.warn('file duplicated with local file. : ' + file.name);
              _op.cancelProcessing(_dz, file, $T('page.album.err.duplicated'));
              return;
            }
          }
          file.checkDupPassed = true;

          // check server files
          hasDuplicateFile(this.cid, file.md5, file.size)
            .then(function(data) {
              if (data.duplicated) {
                console.warn('file duplicated with server file. : ' + file.name);
                _op.cancelProcessing(_dz, file, $T('page.album.err.duplicated'));
                return;
              } else {
                return next();
              }
            })
            .catch(function(e) {
              console.warn('server duplicate check returned error.', e);
              _op.cancelProcessing(_dz, file, $T('page.album.err.preparing'));
              return;
            });
        },
        copyProps(fromFile, toFile) {
          toFile.ext = fromFile.ext;
          toFile.origName = fromFile.origName;
          toFile.origSize = fromFile.origSize;
          toFile.origLastModified = fromFile.origLastModified;
          toFile.md5 = fromFile.md5;
          toFile.token = fromFile.token;
        },
        getVideoThumbnail: function(file, success, failure) {
          // create a hidden <video> element with video file.
          FrameGrab.blob_to_video(file).then(
            function videoRendered(videoEl) {
              // extract video frame at 1 sec into a 160px image and
              // set to the img element.
              var img = document.createElement('img');
              var frameGrab = new FrameGrab({ video: videoEl });
              frameGrab.grab(img, 1).then(
                function frameGrabbed(itemEntry) {
                  imageUtils.dataURLToCanvas(
                    itemEntry.container.currentSrc,
                    1,
                    function(cv) {
                      success(cv);
                    },
                    function(errType) {
                      failure('failed to write image to canvas.');
                    }
                  );
                },
                function frameFailedToGrab(reason) {
                  console.warn("Can't grab the video frame from file: " + file.name + '. Reason: ' + reason);
                  failure(reason);
                }
              );
            },
            function videoFailedToRender(reason) {
              console.warn("Can't convert the file to a video element: " + file.name + '. Reason: ' + reason);
              failure(reason);
            }
          );
        },
        addPreparing: function(file) {
          if (file.previewElement) {
            file.previewElement.classList.add('preparing');
          }
        },
        removePreparing: function(file) {
          if (file.previewElement) {
            file.previewElement.classList.remove('preparing');
          }
        },
        onThumbnail: function(file, dataUrl) {
          var j, len, ref, thumbnailElement;
          if (file.previewElement) {
            file.previewElement.classList.remove('dz-file-preview');
            ref = file.previewElement.querySelectorAll('[data-dz-thumbnail]');
            for (j = 0, len = ref.length; j < len; j++) {
              thumbnailElement = ref[j];
              thumbnailElement.alt = file.name;
              var img = document.createElement('img');
              img.src = dataUrl;
              thumbnailElement.parentNode.appendChild(img);
              // thumbnailElement.style.backgroundImage = 'url("' + dataUrl + '")';
            }
            return setTimeout(
              (function(_this) {
                return function() {
                  return file.previewElement.classList.add('dz-image-preview');
                };
              })(this),
              1
            );
          }
        },
      },
    };
  },
  computed: {
    targetExts() {
      if (this.type == 'file') return null;

      if (this.type == 'album') {
        let arr = {};
        Object.assign(arr, CONST.accept_album_img_exts);
        Object.assign(arr, CONST.accept_album_movie_exts);
        return this.getExts(arr);
      }
      throw 'unknown upload type :' + this.type;
    },
  },
  methods: {
    setDzErrMsg(k, v) {
      this.errMsgs[k] = v;
      this.errMsgs = Object.assign({}, this.errMsgs);
    },
    clearDzErrMsg(k) {
      delete this.errMsgs[k];
      this.errMsgs = Object.assign({}, this.errMsgs);
    },
    checkVolume(volume) {
      if (!volume) {
        this.setDzErrMsg('novolume', $T('common.err.getdata'));
        console.warn(`failed to get volume data for upload.`);
        this.shortMsg(this.errMsgs.novolume, true);
        return false;
      }
      this.clearDzErrMsg('novolume');

      if (volume.usage < volume.max) {
        this.clearDzErrMsg('vmax');
        return true;
      }

      this.setDzErrMsg('vmax', $T('common.err.storage.full'));
      this.shortMsg(this.errMsgs.vmax, true);
      return false;
    },
    getNativeDropZone() {
      return this.$refs.dz.dropzone;
    },
    hasUploadFile() {
      return this.$refs.dz.getQueuedFiles().length > 0;
    },
    hasImgFile() {
      var fs = this.$refs.dz.getQueuedFiles();
      for (var f of fs) {
        var ln = f.name.toLowerCase();
        if (ln.endsWith('.png') || ln.endsWith('.jpg') || ln.endsWith('.jpeg')) {
          return true;
        }
      }
      return false;
    },
    getExts(arr) {
      let exts = [];
      for (var key in arr) {
        exts.push(key);
      }
      exts.sort();

      let r = false;
      for (key of exts) {
        r = (r ? r + ', .' : '.') + key;
      }
      return r;
    },
    validate() {
      if (!this.hasUploadFile()) {
        this.setDzErrMsg('nofile', $T('page.album.err.no-file'));
        return false;
      }
      return true;
    },
    onFileAdded: function(file) {
      var _dz = this.getNativeDropZone();
      var _op = _dz.options;
      var _this = this;

      if (this.maxFileCount <= _dz.getAcceptedFiles().length) {
        this.setDzErrMsg('filecount', $T('page.album.err.max.filecount', { count: this.maxFileCount }));
        _dz.removeFile(file);
        return;
      }

      if (this.hasUploadFile()) {
        this.clearDzErrMsg('filecount');
      }

      var ele = file.previewElement.getElementsByClassName('dz-close')[0];
      ele.addEventListener('click', function() {
        if (!_this.uploading) {
          if (_this.maxFileCount >= _dz.getAcceptedFiles().length) {
            _this.clearDzErrMsg('filecount');
          }
          _dz.removeFile(file);
        }
      });

      // get ext
      var comps = file.name.split('.');
      if (comps.length === 1 || (comps[0] === '' && comps.length === 2)) {
        console.warn('failed to get ext');
        return;
      }
      file.ext = comps.pop().toLowerCase();

      if (!_op.isMovie(file)) {
        return;
      }

      _op.getVideoThumbnail(
        file,
        function(cv) {
          var thumbCv = imageUtils.crop(cv, _op.thumbnailWidth, _op.thumbnailHeight);
          _op.onThumbnail(file, thumbCv.toDataURL('image/png'));
        },
        function(reason) {
          console.warn('failed to get movie thumbnail!' + reason);
        }
      );
    },
    onDuplicate: function(file) {
      console.log('duplicated!!!!!');
    },
    startSending: function(volume) {
      if (!this.checkVolume(volume)) {
        return false;
      }
      this.volume = volume;
      this.uploading = true;
      this.$refs.dz.processQueue();
      return true;
    },
    onSending(file, xhr, formData) {
      var v = this.volume;
      if (v && v.usage + file.origSize > v.max) {
        var _dz = this.$refs.dz.dropzone;
        _dz.options.cancelProcessing(_dz, file, $T('validation.file_size_limit'), true);
        return;
      }

      this.$emit('sending', file, xhr, formData);
      file.sendStarted = true;
      formData.append('type', this.forAlbum ? 'a' : 'f');
      formData.append('md5', file.md5);
      formData.append('size', file.origSize);
      formData.append('name', file.origName);
      formData.append('last_modified', file.origLastModified);
      formData.append('check_dup', this.checkDup);
      formData.append('_token', file.token);

      console.log(`ONSENDING ${file.origName}`);
    },
    onSuccess(file) {
      var data = JSON.parse(file.xhr.response).data;
      this.volume.usage += data.item.filesize;
      this.$emit('success', file, data);
      if (!this.checkVolume(this.volume)) this.cancel();
    },
    cancel: function() {
      var _dz = this.$refs.dz.dropzone;
      var files = _dz.getUploadingFiles();
      for (var file of files) {
        _dz.options.cancelProcessing(_dz, file, $T('page.album.dz.dictUploadCanceled'), true);
      }
      this.uploading = false;
      this.loading = false;
    },
    onCanceled: function(file) {
      if (!file.cancelMsg) return;
      this.setFileErrMsg(file, file.cancelMsg);
    },
    onSendError: function(file, message, xhr) {
      this.$emit('sendErr', file);
      if (file.sendStarted && xhr && xhr.response) {
        var r = JSON.parse(xhr.response);
        if (r.errors) {
          if (r.errors['file'] && r.errors['file'][0] == 'duplicated') {
            // specific duplication
            console.warn('duplicated on upload.');
            this.setFileErrMsg(file, $T('page.album.err.duplicated'));
            this.startSending();
            return;
          }
          if (r.errors['volume'] && r.errors['volume'][0] == 'full') {
            this.setFileErrMsg(file, $T('common.err.storage.full'));
            this.volume.usage = this.volume.max;
            this.checkVolume(this.volume);
            this.cancel();
            return;
          }
        }
      }

      if (!file.sendStarted) return;
      console.warn('failed to upload! :' + file.name);
      this.setFileErrMsg(file, $T('page.album.err.server'));
    },
    setFileErrMsg(file, msg) {
      var j, len, ref, msgElement;
      if (file.previewElement) {
        ref = file.previewElement.querySelectorAll('[data-dz-errormessage]');
        for (j = 0, len = ref.length; j < len; j++) {
          msgElement = ref[j];
          msgElement.textContent = escapeHtml(msg);
        }
      }
    },
    onQueueCompleted: function(file, xhr, formData) {
      this.uploading = false;
      this.$emit('queueComplete', file);
      console.log('upload queue completed!');
    },
    template: function() {
      return `<div class="dz-preview dz-file-preview preparing" style="width: ${this.thumbnailWidth}px; height: ${this.thumbnailHeight}px;">
            <div class="dz-image">
              <div data-dz-thumbnail></div>
            </div>
            <div class="dz-details">
              <div class="dz-size"><span data-dz-size></span></div>
              <div class="dz-filename"><span data-dz-name></span></div>
            </div>
            <div class="dz-preparing"><div role="progressbar" aria-valuemin="0" aria-valuemax="100" class="v-progress-circular v-progress-circular--indeterminate white--text" style="height: 32px; width: 32px;"><svg xmlns="http://www.w3.org/2000/svg" viewBox="22.857142857142858 22.857142857142858 45.714285714285715 45.714285714285715" style="transform: rotate(0deg);"><circle fill="transparent" cx="45.714285714285715" cy="45.714285714285715" r="20" stroke-width="5.714285714285714" stroke-dasharray="125.664" stroke-dashoffset="125.66370614359172px" class="v-progress-circular__overlay"></circle></svg><div class="v-progress-circular__info"></div></div></div>
            <div class="dz-progress"><span class="dz-upload" data-dz-uploadprogress></span></div>
            <div class="dz-close"><i class="v-icon mdi mdi-close-circle"></i></div>
            <div class="dz-error-message"><span data-dz-errormessage></span></div>
            <div class="dz-success-mark"><i class="v-icon mdi mdi-check-outline"></i></div>
            <div class="dz-error-mark"><i class="v-icon mdi mdi-alert-circle-outline" style="font-size: 60px;"></i></div>
            <div class="dz-comp"></div>
          </div>`;
    },
    onThumbnail: function(file, dataUrl) {
      this.$refs.dz.options.onThumbnail(file, dataUrl);
    },
    clearFiles: function() {
      if (this.uploading) return;
      this.errMsgs = {};
      this.$refs.dz.dropzone.removeAllFiles(false);
    },
    hasQueuedFiles: function() {
      return this.$refs.dz.getQueuedFiles().length > 0;
    },
  },
};
</script>

<style scoped>
.c-dz {
  position: relative;
}
.c-erase {
  position: absolute;
  top: 5px;
  right: 5px;
  z-index: 5;
}
.c-msg-area {
  margin: 5px 0 0 0px;
}
.c-hint {
  color: rgba(0, 0, 0, 0.54);
}
.c-btm {
  padding: 3px 5px 3px 0;
}
</style>
